import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  fetchOtoSettings,
} from "../../../store/custom-actions/settings-actions/oto-settings/GetOtoSettingsSlicer";
import {
  updateOtoSettings,
} from "../../../store/custom-actions/settings-actions/oto-settings/PostOtoSettingsSlicer";
import {
  testOtoConnection,
} from "../../../store/custom-actions/settings-actions/oto-settings/TestOtoConnectionSlicer";
import SubmitButton from "../../components/ui/SubmitButton";
import FormField from "../../components/ui/FormField";
import SelectBox from "../../components/ui/SelectBox";
import CheckboxField from "../../components/ui/CheckboxField";
import LoadingScreen from "../../components/loading-screen/LoadingScreen";

function OtoSettingsPage() {
  const { t } = useTranslation();
  const { loading: fetchLoading, data: settingData } = useSelector(
    (state) => state.getOtoSettingsSlicer
  );
  const { loading: submitLoading } = useSelector(
    (state) => state.postOtoSettingsSlicer
  );
  const { loading: testLoading, testResult, error: testError } = useSelector(
    (state) => state.testOtoConnectionSlicer
  );
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm();

  const otoMode = watch("oto_mode");

  const otoFields = useMemo(() => [
    { label: "Sandbox Refresh Token", name: "oto_sandbox_refresh_token", required: false, type: "text" },
    { label: "Sandbox Webhook Secret", name: "oto_sandbox_webhook_secret", required: false, type: "text" },
    { label: "Live Refresh Token", name: "oto_live_refresh_token", required: false, type: "text" },
    { label: "Live Webhook Secret", name: "oto_live_webhook_secret", required: false, type: "text" },
    { label: "Webhook URL (Read-only)", name: "oto_webhook_url", required: false, disabled: true },
    {
      label: "OTO Mode",
      name: "oto_mode",
      type: "selectbox",
      required: false,
      data: [
        { id: "sandbox", name: "Sandbox" },
        { id: "live", name: "Live" },
      ],
    },
    { label: "Enable OTO Shipping", name: "enable_oto_shipping", type: "checkbox" },
  ], []);

  const warehouseFields = useMemo(() => [
    { label: "City", name: "oto_default_warehouse_address.city", required: false },
    { label: "State", name: "oto_default_warehouse_address.state", required: false },
    { label: "Street", name: "oto_default_warehouse_address.street", required: false },
    { label: "Latitude", name: "oto_default_warehouse_address.lat", required: false, type: "number" },
    { label: "Longitude", name: "oto_default_warehouse_address.lng", required: false, type: "number" },
  ], []);

  useEffect(() => {
    if (settingData) {
      // Set simple fields
      otoFields.forEach(({ name, type }) => {
        if (type !== "checkbox" && type !== "selectbox") {
          const value = settingData[name];
          if (typeof value === "string" || typeof value === "number") {
            setValue(name, value);
          }
        }
      });

      // Set checkbox
      if (settingData.enable_oto_shipping !== undefined) {
        setValue("enable_oto_shipping", settingData.enable_oto_shipping);
      }

      // Set selectbox
      if (settingData.oto_mode) {
        setValue("oto_mode", settingData.oto_mode);
      }

      // Set warehouse address
      if (settingData.oto_default_warehouse_address) {
        const addr = settingData.oto_default_warehouse_address;
        setValue("oto_default_warehouse_address.city", addr.city || "");
        setValue("oto_default_warehouse_address.state", addr.state || "");
        setValue("oto_default_warehouse_address.street", addr.street || "");
        setValue("oto_default_warehouse_address.lat", addr.lat || "");
        setValue("oto_default_warehouse_address.lng", addr.lng || "");
      }

      // Set webhook URL (read-only)
      if (settingData.oto_webhook_url) {
        setValue("oto_webhook_url", settingData.oto_webhook_url);
      }
    }
  }, [settingData, setValue, otoFields]);

  useEffect(() => {
    dispatch(fetchOtoSettings());
  }, [dispatch]);

  const onSubmit = async (data) => {
    // Prepare payload
    const payload = {};

    // Add simple fields
    otoFields.forEach(({ name, type, disabled }) => {
      if (type !== "checkbox" && type !== "selectbox" && !disabled && data[name]) {
        payload[name] = data[name];
      } else if (type === "selectbox" && data[name]) {
        payload[name] = data[name];
      } else if (type === "checkbox") {
        payload[name] = data[name] || false;
      }
    });

    // Add warehouse address if any field is filled
    const warehouseData = {};
    let hasWarehouseData = false;
    warehouseFields.forEach(({ name }) => {
      const fieldName = name.split(".").pop();
      const value = data[name];
      if (value !== undefined && value !== null && value !== "") {
        warehouseData[fieldName] = value;
        hasWarehouseData = true;
      }
    });

    if (hasWarehouseData) {
      payload.oto_default_warehouse_address = warehouseData;
    }

    const result = await dispatch(updateOtoSettings(payload));
    if (result.payload?.success) {
      dispatch(fetchOtoSettings());
      toast.success(t(result.payload.message || "OTO shipping settings updated successfully."), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else if (result.payload?.errors) {
      // Handle validation errors
      const errorMessages = Object.values(result.payload.errors).flat();
      errorMessages.forEach((msg) => {
        toast.error(t(msg), {
          position: "top-right",
          autoClose: 5000,
        });
      });
    }
  };

  const handleTestConnection = async () => {
    const mode = otoMode || settingData?.oto_mode || "sandbox";
    const result = await dispatch(testOtoConnection({ mode }));
    
    if (result.payload?.success) {
      toast.success(t(result.payload.message || "Connection test successful"), {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } else {
      const errorMsg = result.payload?.message || result.payload?.data?.error || "Connection test failed";
      toast.error(t(errorMsg), {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  if (fetchLoading) {
    return <LoadingScreen loading={true} />;
  }

  return (
    <>
      <PageTitle
        motherMenu={"Settings"}
        activeMenu={"OTO Shipping Settings"}
        customClasses={"mb-0"}
      />
      <div className="row">
        <div className="col-xl-12 col-xxl-12">
          <form id="submit-form" onSubmit={handleSubmit(onSubmit)}>
            <div className="p-2 mb-3 mt-0">
              <SubmitButton
                isLoading={submitLoading}
                form="submit-form"
              />
              <button
                type="button"
                className="btn btn-info mx-2"
                onClick={handleTestConnection}
                disabled={testLoading || submitLoading}
              >
                {testLoading ? t("Testing...") : t("Test Connection")}
              </button>
            </div>

            {/* Test Result Display */}
            {testResult && (
              <div className="card mb-3">
                <div className="card-body">
                  <h6 className="text-success">{t("Connection Test Result")}</h6>
                  <p className="mb-1"><strong>{t("Mode")}:</strong> {testResult.mode}</p>
                  <p className="mb-1"><strong>{t("API URL")}:</strong> {testResult.api_url}</p>
                  <p className="mb-1"><strong>{t("Status")}:</strong> {testResult.status}</p>
                  {testResult.test_result && (
                    <>
                      <p className="mb-1">
                        <strong>{t("Access Token Obtained")}:</strong>{" "}
                        {testResult.test_result.access_token_obtained ? (
                          <span className="text-success">{t("Yes")}</span>
                        ) : (
                          <span className="text-danger">{t("No")}</span>
                        )}
                      </p>
                      {testResult.test_result.account_info && (
                        <div className="mt-3">
                          <h6 className="mb-2">{t("Account Information")}</h6>
                          <p className="mb-1"><strong>{t("Name")}:</strong> {testResult.test_result.account_info.name}</p>
                          <p className="mb-1"><strong>{t("Email")}:</strong> {testResult.test_result.account_info.email}</p>
                          <p className="mb-1"><strong>{t("Mobile Number")}:</strong> {testResult.test_result.account_info.mobile_number}</p>
                          <p className="mb-1"><strong>{t("Package Name")}:</strong> {testResult.test_result.account_info.package_name}</p>
                          <p className="mb-1"><strong>{t("Remaining Credit")}:</strong> {testResult.test_result.account_info.remaining_credit}</p>
                          <p className="mb-1"><strong>{t("Remaining Free Shipments")}:</strong> {testResult.test_result.account_info.remaining_free_shipments}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Basic OTO Settings */}
            <div className="card">
              <h5 className="card-title p-4">{t("OTO Shipping Settings")}</h5>
              <div className="card-body">
                <div className="row">
                  {otoFields.map(({ label, name, type, required, data, disabled }) => (
                    <div className="col-md-6" key={name}>
                      {type === "checkbox" ? (
                        <CheckboxField
                          label={label}
                          name={name}
                          register={register}
                          errors={errors}
                        />
                      ) : type === "selectbox" ? (
                        <SelectBox
                          label={label}
                          name={name}
                          register={register}
                          required={required}
                          errors={errors}
                          data={data}
                          placeholder={`Select ${label}`}
                          showLabel={true}
                        />
                      ) : (
                        <FormField
                          label={label}
                          name={name}
                          register={register}
                          required={required}
                          errors={errors}
                          type={type || "text"}
                          disabled={disabled}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Warehouse Address */}
            <div className="card">
              <h5 className="card-title p-4">{t("Default Warehouse Address")}</h5>
              <div className="card-body">
                <div className="row">
                  {warehouseFields.map(({ label, name, required, type }) => (
                    <div className="col-md-6" key={name}>
                      <FormField
                        label={label}
                        name={name}
                        register={register}
                        required={required}
                        errors={errors}
                        type={type || "text"}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export default OtoSettingsPage;
